import { useTranslation } from 'react-i18next';

export default function About() {
  const { t } = useTranslation('home');

  const companyInfo = {
    introduction: t('about.introduction'),
    vision: t('about.visionText'),
    mission: t('about.missionText')
  };

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: t('about.values.0.title'),
      description: t('about.values.0.description')
    },
    {
      icon: 'ri-team-line',
      title: t('about.values.1.title'),
      description: t('about.values.1.description')
    },
    {
      icon: 'ri-customer-service-2-line',
      title: t('about.values.2.title'),
      description: t('about.values.2.description')
    },
    {
      icon: 'ri-trophy-line',
      title: t('about.values.3.title'),
      description: t('about.values.3.description')
    }
  ];

  const stats = [
    {
      number: t('about.stats.0.number'),
      label: t('about.stats.0.label')
    },
    {
      number: t('about.stats.1.number'),
      label: t('about.stats.1.label')
    },
    {
      number: t('about.stats.2.number'),
      label: t('about.stats.2.label')
    },
    {
      number: t('about.stats.3.number'),
      label: t('about.stats.3.label')
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('about.title')}</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-indigo-500 to-purple-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>

        {/* Company Introduction */}
        <div className="mb-20">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('about.companyIntro')}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {companyInfo.introduction}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-eye-line text-indigo-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('about.vision')}</h4>
                      <p className="text-gray-600 text-sm">{companyInfo.vision}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-flag-line text-purple-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{t('about.mission')}</h4>
                      <p className="text-gray-600 text-sm">{companyInfo.mission}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative h-96 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20technology%20office%20with%20professional%20team%20working%20on%20computers%20and%20digital%20displays%20showing%20data%20analytics%20and%20business%20solutions%2C%20bright%20and%20clean%20corporate%20environment%20with%20glass%20walls%20and%20contemporary%20design&width=600&height=800&seq=about-company-image&orientation=portrait"
                  alt={t('about.title')}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Core Values */}
        <div>
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">{t('about.coreValues')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h4>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
